import React, { useState, useCallback, useEffect, useReducer } from "react";
import { useDispatch } from "react-redux";
import { Calendar, Button, Select, Input } from "antd";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import type { CalendarProps } from "antd";
import {
    IDatePickerInputValidationInitialState,
    TCustomDatePickerInputAction,
    TDatePickerInputFocusState,
    TDatePickerInputValidationAction,
} from "@ts/common-types";
import {
    CUST_DATE_PICKER_INPUT_VALIDATION_INITIAL_STATE,
    CUSTOM_DATE_PICKER_INPUT_FIELDS,
    CustomDatePickerInputInitialState,
    DATE_PICKER_FOCUSED_INPUT,
    DATE_PICKER_PRESET_PERIODS,
    DateTimeFormats,
} from "@ts/constants";
import { updateSelectedDateRange } from "@features/selectedDataSlice";
import { resetRevstackDashboardWidgetData } from "@features/revstackWidgetsSlice";
import "@/scss/custom-range-date-picker.scss";
import { applyCurrentFiltersWithHash } from "@/ts/revstack/filters";


dayjs.extend(isBetween);

interface DateRange {
    start: Dayjs | null;
    end: Dayjs | null;
}

interface DateRangePickerProps {
    onApply?: (primaryRange: DateRange, compareRange: DateRange | null) => void;
    onCancel?: () => void;
    initialPrimary?: DateRange | null;
    initialCompare?: DateRange | null;
    initialPresetPeriod?: DATE_PICKER_PRESET_PERIODS
}

type PresetPeriod = DATE_PICKER_PRESET_PERIODS
type ComparePeriod = typeof DATE_PICKER_PRESET_PERIODS.PREVIOUS_PERIOD | typeof DATE_PICKER_PRESET_PERIODS.CUSTOM;

const RevstackRangeDatePicker: React.FC<DateRangePickerProps> = ({
    onApply,
    onCancel,
    initialPrimary,
    initialCompare,
    initialPresetPeriod
}) => {
    
    const dispatch = useDispatch();
    const [primaryPeriod, setPrimaryPeriod] = useState<PresetPeriod>(initialPresetPeriod);
    const [comparePeriod, setComparePeriod] =
        useState<ComparePeriod>(DATE_PICKER_PRESET_PERIODS.PREVIOUS_PERIOD);
    const [enableComparison, setEnableComparison] = useState(true);
    const [dateRangeError, setDateRangeError] = useState("")
    const dateFormatForDisplay = DateTimeFormats.DATE_PICKER_INPUT;

    const [primaryRange, setPrimaryRange] = useState<DateRange>(
        initialPrimary || {
            start: dayjs().subtract(7, 'day'),
            end: dayjs().subtract(1, 'day'),
        }
    );

    const [compareRange, setCompareRange] = useState<DateRange>(
        initialCompare || {
            start: dayjs().subtract(14, 'day'),
            end: dayjs().subtract(8, 'day'),
        }
    );

    const [primaryCalendarDate, setPrimaryCalendarDate] = useState(
        initialPrimary?.start || dayjs().subtract(7, 'day')
    );
    const [compareCalendarDate, setCompareCalendarDate] = useState(
        initialCompare?.start || dayjs().subtract(14, 'day')
    );

    const dateInputReducer = (
        state: typeof CustomDatePickerInputInitialState,
        action: TCustomDatePickerInputAction
    ): typeof CustomDatePickerInputInitialState => {
        switch (action.type) {
            case "SET_INPUT":
                return {
                    ...state,
                    [action.field]: action.value,
                };
            default:
                return state;
        }
    };

    const [datesInput, dispatchDatesInput] = useReducer(
        dateInputReducer,
        CustomDatePickerInputInitialState
    );

    const dateInputValidationReducer = (
        state: IDatePickerInputValidationInitialState,
        action: TDatePickerInputValidationAction
    ): IDatePickerInputValidationInitialState => {
        switch (action.type) {
            case "SET_INVALID":
                return {
                    ...state,
                    [action.field]: {
                        invalid: action.value.invalid,
                        message: action.value.message,
                    },
                };
            case "RESET_ALL":
                return {
                    primaryStart: { invalid: false, message: "" },
                    primaryEnd: { invalid: false, message: "" },
                    compareStart: { invalid: false, message: "" },
                    compareEnd: { invalid: false, message: "" },
                };
            default:
                return state;
        }
    };

    const [validationState, dispatchValidation] = useReducer(
        dateInputValidationReducer,
        CUST_DATE_PICKER_INPUT_VALIDATION_INITIAL_STATE
    );

    const [focusedInput, setFocusedInput] =
        useState<TDatePickerInputFocusState>(DATE_PICKER_FOCUSED_INPUT.PRIMARY);

    useEffect(() => {
        if (initialPrimary) {
            setPrimaryRange(initialPrimary);
            setPrimaryCalendarDate(initialPrimary.start || dayjs());
        }
        if (initialCompare) {
            setCompareRange(initialCompare);
            setCompareCalendarDate(initialCompare.start || dayjs());
            setEnableComparison(true);
        }
        if(initialPresetPeriod) {
            setPrimaryPeriod(initialPresetPeriod);
        }
    }, [initialPrimary, initialCompare, initialPresetPeriod]);

    useEffect(() => {
        dispatchDatesInput({
            type: "SET_INPUT",
            field: CUSTOM_DATE_PICKER_INPUT_FIELDS.PrimaryStart,
            value: primaryRange.start
                ? primaryRange.start.format(DateTimeFormats.DATE_PICKER_INPUT)
                : "",
        });

        dispatchDatesInput({
            type: "SET_INPUT",
            field: CUSTOM_DATE_PICKER_INPUT_FIELDS.PrimaryEnd,
            value: primaryRange.end ? primaryRange.end.format(DateTimeFormats.DATE_PICKER_INPUT) : "",
        });
    }, [primaryRange]);

    useEffect(() => {
        dispatchDatesInput({
            type: "SET_INPUT",
            field: CUSTOM_DATE_PICKER_INPUT_FIELDS.CompareStart,
            value: compareRange.start
                ? compareRange.start.format(DateTimeFormats.DATE_PICKER_INPUT)
                : "",
        });

        dispatchDatesInput({
            type: "SET_INPUT",
            field: CUSTOM_DATE_PICKER_INPUT_FIELDS.CompareEnd,
            value: compareRange.end ? compareRange.end.format(DateTimeFormats.DATE_PICKER_INPUT) : "",
        });
    }, [compareRange]);

    // Automatically calculate compare range based on primary range
    useEffect(() => {
        if (
            primaryRange.start &&
            primaryRange.end &&
            comparePeriod !== DATE_PICKER_PRESET_PERIODS.CUSTOM
        ) {
            const newCompareRange = getComparisonRangeForPrimaryPeriod(
                primaryPeriod,
                primaryRange
            );
            setCompareRange(newCompareRange);
            setCompareCalendarDate(newCompareRange.start || dayjs());
        }
    }, [primaryRange, primaryPeriod, comparePeriod]);

    useEffect(() => {
        if (focusedInput === DATE_PICKER_FOCUSED_INPUT.COMPARE && compareRange?.start) {
            setPrimaryCalendarDate(compareRange.start.startOf("month"));
        } else if (focusedInput === DATE_PICKER_FOCUSED_INPUT.PRIMARY && primaryRange?.start) {
            setPrimaryCalendarDate(primaryRange.start.startOf("month"));
        }
    }, [focusedInput]);

    useEffect(() => {
        if (primaryPeriod === DATE_PICKER_PRESET_PERIODS.CUSTOM && primaryRange?.start) {
            setPrimaryCalendarDate(primaryRange.start.startOf("month"));
        }

        if (comparePeriod === DATE_PICKER_PRESET_PERIODS.CUSTOM && compareRange?.start) {
            setCompareCalendarDate(compareRange.start.startOf("month"));
        }
    }, [primaryRange.start, primaryPeriod, compareRange.start, comparePeriod]);

    // Calculate comparison range based on primary period
    const getComparisonRangeForPrimaryPeriod = (
        primaryPeriod: PresetPeriod,
        baseRange: DateRange
    ): DateRange => {
        if (!baseRange.start || !baseRange.end) return baseRange;

        switch (primaryPeriod) {
            case DATE_PICKER_PRESET_PERIODS.YESTERDAY:
                // Yesterday: 25 Jun 2025 vs 24 Jun 2025 (no overlap)
                return {
                    start: baseRange.start.subtract(1, "day"),
                    end: baseRange.end.subtract(1, "day"),
                };
            case DATE_PICKER_PRESET_PERIODS.LAST_7_DAYS:
                // Last 7 Days: 20-26 Jun 2025 vs 13-19 Jun 2025 (no overlap)
                const l7dDiff = baseRange.end.diff(baseRange.start, "day");
                return {
                    start: baseRange.start.subtract(l7dDiff + 1, "day"),
                    end: baseRange.start.subtract(1, "day"),
                };
            case DATE_PICKER_PRESET_PERIODS.LAST_30_DAYS:
                // Last 30 Days: 1-26 Jun 2025 vs 1-26 May 2025 (no overlap)
                const l30dDiff = baseRange.end.diff(baseRange.start, "day");
                return {
                    start: baseRange.start.subtract(l30dDiff + 1, "day"),
                    end: baseRange.start.subtract(1, "day"),
                };
            case DATE_PICKER_PRESET_PERIODS.MONTH_TO_DATE:
                // Month to Date: 1-26 Jun 2025 vs 1-26 May 2025 (no overlap)
                return {
                    start: baseRange.start.subtract(1, "month"),
                    end: baseRange.end.subtract(1, "month"),
                };
            case DATE_PICKER_PRESET_PERIODS.LAST_MONTH:
                // Last Month: 1-31 May 2025 vs 1-30 Apr 2025 (no overlap)
                const previousMonth = baseRange.start.subtract(1, "month");
                return {
                    start: previousMonth.startOf("month"),
                    end: previousMonth.endOf("month"),
                };
            case DATE_PICKER_PRESET_PERIODS.LAST_3_MONTHS:
                // Last 3 Months: 1 Jan-26 Jun 2025 vs 1 Jan-26 Jun 2024 (no overlap)
                const startOfCompare = baseRange.start
                    .subtract(3, "month")
                    .startOf("month");
                const endOfCompare = baseRange.start
                    .subtract(1, "month")
                    .endOf("month");
                return {
                    start: startOfCompare,
                    end: endOfCompare,
                };
            case DATE_PICKER_PRESET_PERIODS.YEAR_TO_DATE:
                // Year to Date: Apr-Jun 2025 vs Jan-Mar 2025 (no overlap)
                return {
                    start: baseRange.start.subtract(1, "year"),
                    end: baseRange.end.subtract(1, "year"),
                };
            case DATE_PICKER_PRESET_PERIODS.LAST_12_MONTHS:
                // Last 12 Months: Jul 2024-Jun 2025 vs Jul 2023-Jun 2024 (no overlap)
                return {
                    start: baseRange.start.subtract(1, "year"),
                    end: baseRange.end.subtract(1, "year"),
                };
            case DATE_PICKER_PRESET_PERIODS.CUSTOM:
                // Custom: 10-20 Jun 2025 (11 days) vs 30 May-9 Jun 2025 (no overlap)
                const diffDays = baseRange.end.diff(baseRange.start, "day");
                return {
                    start: baseRange.start.subtract(diffDays + 1, "day"),
                    end: baseRange.start.subtract(1, "day"),
                };
            default:
                return baseRange;
        }
    };

const getPresetRange = useCallback(
    (preset: PresetPeriod): DateRange => {
        const today = dayjs();
        const yesterday = today.subtract(1, "day");
        switch (preset) {
            case DATE_PICKER_PRESET_PERIODS.YESTERDAY:
                return {
                    start: yesterday,
                    end: yesterday,
                };
            case DATE_PICKER_PRESET_PERIODS.LAST_7_DAYS:
                return {
                    start: yesterday.subtract(6, "day"),
                    end: yesterday,
                };
            case DATE_PICKER_PRESET_PERIODS.LAST_30_DAYS:
                return {
                    start: yesterday.subtract(29, "day"),
                    end: yesterday,
                };
            case DATE_PICKER_PRESET_PERIODS.MONTH_TO_DATE:
                // If today is the 1st of the month, yesterday would be the last day of previous month
                // In this case, return the entire previous month
                if (today.date() === 1) {
                    return {
                        start: yesterday.startOf("month"),
                        end: yesterday.endOf("month"),
                    };
                }
                // Otherwise, return from start of current month to yesterday
                return {
                    start: yesterday.startOf("month"),
                    end: yesterday,
                };
            case DATE_PICKER_PRESET_PERIODS.LAST_MONTH:
                // Use today to determine the month, then get the previous month
                // This avoids day-of-month overflow issues (e.g., Oct 31 - 1 month = Sep 30)
                return {
                    start: today.subtract(1, "month").startOf("month"),
                    end: today.subtract(1, "month").endOf("month"),
                };
            case DATE_PICKER_PRESET_PERIODS.LAST_3_MONTHS:
                // Use today to determine the month to avoid day overflow issues
                return {
                    start: today.subtract(3, "month").startOf("month"),
                    end: today.subtract(1, "month").endOf("month"),
                };
            case DATE_PICKER_PRESET_PERIODS.YEAR_TO_DATE:
                // If today is January 1st, yesterday would be the last day of previous year
                // In this case, return the entire previous year
                if (today.month() === 0 && today.date() === 1) {
                    return {
                        start: yesterday.startOf("year"),
                        end: yesterday.endOf("year"),
                    };
                }
                // Otherwise, return from start of current year to yesterday
                return {
                    start: yesterday.startOf("year"),
                    end: yesterday,
                };
            case DATE_PICKER_PRESET_PERIODS.LAST_12_MONTHS:
                // Use today to determine the month to avoid day overflow issues
                return {
                    start: today.subtract(12, "month").startOf("month"),
                    end: today.subtract(1, "month").endOf("month"),
                };
            default:
                return primaryRange;
        }
    },
    [primaryRange]
);


    const handlePrimaryPeriodChange = (value: PresetPeriod) => {
        setPrimaryPeriod(value);
        if (value !== DATE_PICKER_PRESET_PERIODS.CUSTOM) {
            const newRange = getPresetRange(value);
            dispatchValidation({
                type: "RESET_ALL",
            });
            setPrimaryRange(newRange);
            setPrimaryCalendarDate(newRange.start || dayjs());
            setDateRangeError(null);
        }
    };

    const handleComparePeriodChange = (value: ComparePeriod) => {
        setComparePeriod(value);
        setEnableComparison(true);

        if (value === DATE_PICKER_PRESET_PERIODS.CUSTOM) {
            // For custom comparison, keep the current compare range but allow manual editing
        }
        // For previous-period, the range will be calculated automatically by the useEffect
        setDateRangeError(null);
    };

    const handlePrimaryDateSelect = (date: Dayjs) => {
        if (primaryPeriod === DATE_PICKER_PRESET_PERIODS.CUSTOM) {
            // For previous-period compare mode, allow overlap and recalculate compare range
            if (comparePeriod === DATE_PICKER_PRESET_PERIODS.PREVIOUS_PERIOD) {
                if (!primaryRange.start || (primaryRange.start && primaryRange.end)) {
                    setPrimaryRange({ start: date, end: null });
                } else if (primaryRange.start && !primaryRange.end) {
                    const tentativeStart = date.isBefore(primaryRange.start)
                        ? date
                        : primaryRange.start;
                    const tentativeEnd = date.isAfter(primaryRange.start)
                        ? date
                        : primaryRange.start;

                    setPrimaryRange({ start: tentativeStart, end: tentativeEnd });

                    // Recalculate compare range if there's overlap
                    if (hasOverlap({ start: tentativeStart, end: tentativeEnd }, compareRange)) {
                        const newCompareRange = getComparisonRangeForPrimaryPeriod(
                            primaryPeriod,
                            { start: tentativeStart, end: tentativeEnd }
                        );
                        setCompareRange(newCompareRange);
                        setCompareCalendarDate(newCompareRange.start || dayjs());
                        setDateRangeError(null);
                    }
                }
            } else {
                // For custom compare mode, prevent overlap
                if (isInRange(date, compareRange)) return;

                if (!primaryRange.start || (primaryRange.start && primaryRange.end)) {
                    setPrimaryRange({ start: date, end: null });
                } else if (primaryRange.start && !primaryRange.end) {
                    const tentativeStart = date.isBefore(primaryRange.start)
                        ? date
                        : primaryRange.start;
                    const tentativeEnd = date.isAfter(primaryRange.start)
                        ? date
                        : primaryRange.start;

                    if (hasOverlap({ start: tentativeStart, end: tentativeEnd }, compareRange)) return;

                    setPrimaryRange({ start: tentativeStart, end: tentativeEnd });

                    setDateRangeError(null);
                }
            }
        }
    };

    const handleCompareDateSelect = (date: Dayjs) => {
        if (comparePeriod === DATE_PICKER_PRESET_PERIODS.CUSTOM) {
            // Don't allow selecting dates that are >= primary start date
            if (primaryRange.start && (date.isAfter(primaryRange.start) || date.isSame(primaryRange.start, 'day'))) {
                return;
            }

            // Don't allow selecting overlapping primary dates
            if (isInRange(date, primaryRange)) return;

            if (!compareRange.start || (compareRange.start && compareRange.end)) {
                setCompareRange({ start: date, end: null });
            } else if (compareRange.start && !compareRange.end) {
                const tentativeStart = date.isBefore(compareRange.start)
                    ? date
                    : compareRange.start;
                const tentativeEnd = date.isAfter(compareRange.start)
                    ? date
                    : compareRange.start;

                // Check if tentative end is >= primary start
                if (primaryRange.start && (tentativeEnd.isAfter(primaryRange.start) || tentativeEnd.isSame(primaryRange.start, 'day'))) {
                    return;
                }

                if (hasOverlap({ start: tentativeStart, end: tentativeEnd }, primaryRange)) return;

                setCompareRange({ start: tentativeStart, end: tentativeEnd });

                dispatchValidation({
                    type: "SET_INVALID",
                    field: "compareStart",
                    value: { invalid: false, message: "" },
                });

                dispatchValidation({
                    type: "SET_INVALID",
                    field: "compareEnd",
                    value: { invalid: false, message: "" },
                });
            }
        }
    };

    const isInRange = (date: Dayjs, range: DateRange): boolean => {
        if (!range.start) return false;
        if (!range.end) return date.isSame(range.start, "day");
        return date.isBetween(range.start, range.end, "day", "[]");
    };

    const isRangeStart = (date: Dayjs, range: DateRange): boolean => {
        return range.start ? date.isSame(range.start, "day") : false;
    };

    const isRangeEnd = (date: Dayjs, range: DateRange): boolean => {
        return range.end ? date.isSame(range.end, "day") : false;
    };

    const areDatesAdjacent = (d1: Dayjs | null, d2: Dayjs | null) =>
        d1 && d2 && Math.abs(d1.diff(d2, "day")) === 1;

    // Enhanced cell renderer to show both primary and compare ranges
    const dualRangeDateCellRender: CalendarProps<Dayjs>["fullCellRender"] = (
        date,
        info
    ) => {
        const isInPrimaryRange = isInRange(date, primaryRange);
        const isInCompareRange =
            enableComparison && compareRange.start
                ? isInRange(date, {
                      start: compareRange.start,
                      end: compareRange.end || compareRange.start,
                  })
                : false;
        const isPrimaryStart = isRangeStart(date, primaryRange);
        const isPrimaryEnd = isRangeEnd(date, primaryRange);
        const isCompareStart =
            enableComparison && compareRange.start
                ? isRangeStart(date, compareRange)
                : false;
        const isCompareEnd =
            enableComparison && compareRange.end
                ? isRangeEnd(date, compareRange)
                : false;

        // Determine styling based on which range(s) the date belongs to
        let className = "date-cell";
        let rangeType = "";

        if (disabledDate(date)) {
            className += " disabled-date";
        }

        if (isInPrimaryRange && isInCompareRange) {
            // Date is in both ranges (overlap)
            className += " both-ranges";
            rangeType = "both";
        } else if (isInPrimaryRange) {
            // Date is only in primary range
            className +=
                focusedInput === DATE_PICKER_FOCUSED_INPUT.COMPARE
                    ? " primary-range primary-range-ocapity"
                    : " primary-range";
            rangeType = DATE_PICKER_FOCUSED_INPUT.PRIMARY;
        } else if (isInCompareRange) {
            // Date is only in compare range
            className +=
                focusedInput === DATE_PICKER_FOCUSED_INPUT.PRIMARY
                    ? " compare-range compare-range-opacity"
                    : " compare-range";
            rangeType = DATE_PICKER_FOCUSED_INPUT.COMPARE;
        } else {
            // Date is not in any range
            className += " no-range";
        }

        // Add endpoint styling
        if (isPrimaryStart || isCompareStart) {
            className += " range-startpoint";
        }
        if (isPrimaryEnd || isCompareEnd) {
            className += " range-endpoint";
        }

        // Logic to add 'adjacent-break' when single-day ranges are adjacent
        const isSinglePrimary =
            primaryRange.start &&
            primaryRange.end &&
            primaryRange.start.isSame(primaryRange.end, "day");

        const isSingleCompare =
            compareRange.start &&
            compareRange.end &&
            compareRange.start.isSame(compareRange.end, "day");

        const isAdjacent =
            isSinglePrimary &&
            isSingleCompare &&
            areDatesAdjacent(primaryRange.start, compareRange.start);

        if (isAdjacent) {
            className += " adjacent-break";
        }

        const getTooltipText = () => {
            if (rangeType === DATE_PICKER_FOCUSED_INPUT.PRIMARY) return "Primary range";
            if (rangeType === DATE_PICKER_FOCUSED_INPUT.COMPARE) return "Compare range";
            return "No range";
        };

        return (
            <div
                className={`
          ${className}
          ${info.type === "date" ? "" : "text-muted"}
        `}
                onClick={() => {
                    if (disabledDate(date)) return; // Prevent selecting disabled dates
                    if (
                        focusedInput === DATE_PICKER_FOCUSED_INPUT.COMPARE &&
                        comparePeriod === DATE_PICKER_PRESET_PERIODS.CUSTOM
                    ) {
                        handleCompareDateSelect(date);
                    } else if (
                        focusedInput === DATE_PICKER_FOCUSED_INPUT.PRIMARY &&
                        primaryPeriod === DATE_PICKER_PRESET_PERIODS.CUSTOM
                    ) {
                        handlePrimaryDateSelect(date);
                    }
                }}
                title={`${date.format(DateTimeFormats.DATE_PICKER_TOOLTIP)} - ${getTooltipText()}`}
            >
                {date.date()}
            </div>
        );
    };

    const handleDateInputChange = (
        val: string,
        isStart: boolean,
        isCompareSection: boolean,
        range: DateRange,
        otherRange: DateRange,
        onRangeChange: (range: DateRange) => void
    ) => {
        const field = isCompareSection
            ? isStart
                ? CUSTOM_DATE_PICKER_INPUT_FIELDS.CompareStart
                : CUSTOM_DATE_PICKER_INPUT_FIELDS.CompareEnd
            : isStart
                ? CUSTOM_DATE_PICKER_INPUT_FIELDS.PrimaryStart
                : CUSTOM_DATE_PICKER_INPUT_FIELDS.PrimaryEnd;

        dispatchDatesInput({ type: "SET_INPUT", field, value: val });

        const newDate = dayjs(val, DateTimeFormats.DATE_PICKER_INPUT, true);
        const isValid = newDate.isValid();

        const validationKey: keyof IDatePickerInputValidationInitialState = isCompareSection
            ? isStart ? "compareStart" : "compareEnd"
            : isStart ? "primaryStart" : "primaryEnd";

        if (!isValid) {
            dispatchValidation({
                type: "SET_INVALID",
                field: validationKey,
                value: { invalid: true, message: "Invalid Date Format!" },
            });
            return;
        }

        // Reject future dates (only allow up to T-1/yesterday)
        if (newDate.isAfter((dayjs()).subtract(1, "day").endOf("day"))) {
            dispatchValidation({
                type: "SET_INVALID",
                field: validationKey,
                value: { invalid: true, message: "Date not allowed!" },
            });
            return;
        }

        const updatedRange = { ...range, [isStart ? "start" : "end"]: newDate };

        // Logical order check within the same range
        if (updatedRange.start && updatedRange.end && updatedRange.start.isAfter(updatedRange.end)) {
            dispatchValidation({
                type: "SET_INVALID",
                field: validationKey,
                value: {
                    invalid: true,
                    message: isStart ? "Invalid Start Date!" : "Invalid End Date!",
                },
            });
            return;
        }

        // For compare range in custom mode, ensure dates are before primary start
        if (isCompareSection && comparePeriod === DATE_PICKER_PRESET_PERIODS.CUSTOM && primaryRange.start) {
            if (newDate.isAfter(primaryRange.start) || newDate.isSame(primaryRange.start, 'day')) {
                dispatchValidation({
                    type: "SET_INVALID",
                    field: validationKey,
                    value: {
                        invalid: true,
                        message: "Must be before primary start!",
                    },
                });
                return;
            }
        }

        // For primary range edits in custom mode with previous-period compare
        if (!isCompareSection && primaryPeriod === DATE_PICKER_PRESET_PERIODS.CUSTOM && 
            comparePeriod === DATE_PICKER_PRESET_PERIODS.PREVIOUS_PERIOD) {
            // Check if the updated primary range would overlap with current compare range
            if (
                otherRange.start &&
                otherRange.end &&
                updatedRange.start &&
                updatedRange.end &&
                hasOverlap(updatedRange, otherRange)
            ) {
                // Recalculate compare range based on the new primary range
                const newCompareRange = getComparisonRangeForPrimaryPeriod(
                    primaryPeriod,
                    updatedRange
                );
                setCompareRange(newCompareRange);
                setCompareCalendarDate(newCompareRange.start || dayjs());
            }
        } else {
            // Overlap check for other cases
            if (
                otherRange.start &&
                otherRange.end &&
                updatedRange.start &&
                updatedRange.end &&
                hasOverlap(updatedRange, otherRange)
            ) {
                dispatchValidation({
                    type: "SET_INVALID",
                    field: validationKey,
                    value: {
                        invalid: true,
                        message: "Overlapping range!",
                    },
                });
            }
        }

        // Valid
        dispatchValidation({
            type: "SET_INVALID",
            field: validationKey,
            value: { invalid: false, message: "" },
        });
        setDateRangeError(null);

        // Clear other field's error if applicable
        dispatchValidation({
            type: "SET_INVALID",
            field: isCompareSection
                ? isStart ? "compareEnd" : "compareStart"
                : isStart ? "primaryEnd" : "primaryStart",
            value: { invalid: false, message: "" },
        });

        onRangeChange(updatedRange);
    };


    // Helper function to check if there are any validation errors
    const hasValidationErrors = () => {
        return (
            validationState.primaryStart.invalid ||
            validationState.primaryEnd.invalid ||
            validationState.compareStart.invalid ||
            validationState.compareEnd.invalid
        );
    };

    const handleApply = () => {
        setDateRangeError(null);

        // 1. Check that primary range is complete
        if (!primaryRange.start || !primaryRange.end) {
            setDateRangeError("Primary range is incomplete.");
            return;
        }

        let finalCompareRange: DateRange | null = null;

        // 2. Determine final compare range if comparison is enabled
        if (enableComparison) {
            if (comparePeriod === DATE_PICKER_PRESET_PERIODS.CUSTOM) {
                if (!compareRange.start || !compareRange.end) {
                    setDateRangeError("Comparison range is incomplete.");
                    return;
                }
                finalCompareRange = compareRange;
            } else {
                // For previous-period, calculate based on primary range
                finalCompareRange = getComparisonRangeForPrimaryPeriod(
                    primaryPeriod,
                    primaryRange
                );
            }
        }

        // 3. Check for overlap
        if (
            finalCompareRange &&
            hasOverlap(primaryRange, finalCompareRange)
        ) {
            setDateRangeError("Cannot set overlapping dates!");
            return;
        }

        // 4. Proceed with apply
        if (hasValidationErrors()) {
            return;
        }
        onApply?.(primaryRange, finalCompareRange);

        const dateRange = {
            start: primaryRange.start.format(DateTimeFormats.DATE_PICKER_API),
            end: primaryRange.end.format(DateTimeFormats.DATE_PICKER_API),
        };
        const compareDateRange = finalCompareRange
            ? {
                start: finalCompareRange.start.format(DateTimeFormats.DATE_PICKER_API),
                end: finalCompareRange.end.format(DateTimeFormats.DATE_PICKER_API),
            }
            : null;

        dispatch(resetRevstackDashboardWidgetData());
        dispatch(
            updateSelectedDateRange({
                dateRange,
                compareDateRange,
                selectedRange: primaryPeriod,
            })
        );

        applyCurrentFiltersWithHash({
            dateRange: {
                dateRange,
                compareDateRange,
                selectedRange: primaryPeriod,
            },
        });
    };

    const dateRangeComponent = (
        title: string,
        options: any,
        value: string,
        onChange: any,
        range: DateRange,
        onRangeChange: (range: DateRange) => void,
        isCompareSection: boolean = false
    ) => {
        const showInputFields = true;
        const isReadOnly = value !== DATE_PICKER_PRESET_PERIODS.CUSTOM;

        const startValidationKey: keyof IDatePickerInputValidationInitialState =
            isCompareSection ? "compareStart" : "primaryStart";
        const endValidationKey: keyof IDatePickerInputValidationInitialState =
            isCompareSection ? "compareEnd" : "primaryEnd";

        return (
            <div className="px-3 pt-3">
                <h3 className="h6 fw-bold mb-3">{title}</h3>
                <Select
                    value={value}
                    onChange={(val) => {
                        setFocusedInput(isCompareSection && val === DATE_PICKER_PRESET_PERIODS.CUSTOM 
                            ? DATE_PICKER_FOCUSED_INPUT.COMPARE 
                            : DATE_PICKER_FOCUSED_INPUT.PRIMARY);
                        onChange(val);
                    }}
                    className="w-100 mb-2"
                    size="middle"
                    options={options}
                />
                {showInputFields && (
                    <div
                        className="d-flex flex-column gap-1"
                        style={{ width: "100%" }}
                    >
                        <div className="d-flex align-items-center justify-content-center gap-2">
                            {/* Start Date Input */}
                            <div className="d-flex flex-column custom-date-input-wrapper" style={{ flex: 1 }}>
                                <Input
                                    size="small"
                                    className={`custom-date-input text-center ${validationState[startValidationKey]?.invalid ? "input-error" : ""
                                        } ${focusedInput === (isCompareSection ? DATE_PICKER_FOCUSED_INPUT.COMPARE : DATE_PICKER_FOCUSED_INPUT.PRIMARY) ? "input-focused" : ""}`}
                                    value={
                                        datesInput[
                                        isCompareSection
                                            ? CUSTOM_DATE_PICKER_INPUT_FIELDS.CompareStart
                                            : CUSTOM_DATE_PICKER_INPUT_FIELDS.PrimaryStart
                                        ]
                                    }
                                    readOnly={isReadOnly}
                                    onChange={(e) =>
                                        handleDateInputChange(
                                            e.target.value,
                                            true, // isStart
                                            isCompareSection,
                                            range,
                                            isCompareSection ? primaryRange : compareRange,
                                            onRangeChange
                                        )
                                    }
                                    onClick={() => {
                                        setFocusedInput(isCompareSection ? DATE_PICKER_FOCUSED_INPUT.COMPARE : DATE_PICKER_FOCUSED_INPUT.PRIMARY);
                                        // Sync calendar to the selected range
                                        if (isCompareSection && compareRange?.start) {
                                            setCompareCalendarDate(compareRange.start.startOf("month"));
                                        } else if (!isCompareSection && primaryRange?.start) {
                                            setPrimaryCalendarDate(primaryRange.start.startOf("month"));
                                        }
                                    }}
                                />
                                <div className="date-format-display">{dateFormatForDisplay}</div>
                                <div
                                    className={`input-error-message ${validationState[startValidationKey]?.invalid ? "" : "hidden"
                                        }`}
                                >
                                    {validationState[startValidationKey]?.message}
                                </div>
                            </div>

                            <div className="d-flex" style={{ height: "3.8rem" }}>
                                &rarr;
                            </div>

                            {/* End Date Input */}
                            <div className="d-flex flex-column custom-date-input-wrapper" style={{ flex: 1 }}>
                                <Input
                                    size="small"
                                    className={`custom-date-input text-center ${validationState[endValidationKey]?.invalid ? "input-error" : ""
                                        } ${focusedInput === (isCompareSection ? DATE_PICKER_FOCUSED_INPUT.COMPARE : DATE_PICKER_FOCUSED_INPUT.PRIMARY) ? "input-focused" : ""}`}
                                    value={
                                        datesInput[
                                        isCompareSection
                                            ? CUSTOM_DATE_PICKER_INPUT_FIELDS.CompareEnd
                                            : CUSTOM_DATE_PICKER_INPUT_FIELDS.PrimaryEnd
                                        ]
                                    }
                                    readOnly={isReadOnly}
                                    onChange={(e) =>
                                        handleDateInputChange(
                                            e.target.value,
                                            false, // isStart
                                            isCompareSection,
                                            range,
                                            isCompareSection ? primaryRange : compareRange,
                                            onRangeChange
                                        )
                                    }
                                    onClick={() => {
                                        setFocusedInput(isCompareSection ? DATE_PICKER_FOCUSED_INPUT.COMPARE : DATE_PICKER_FOCUSED_INPUT.PRIMARY);
                                        // Sync calendar to the selected range
                                        if (isCompareSection && compareRange?.start) {
                                            setCompareCalendarDate(compareRange.start.startOf("month"));
                                        } else if (!isCompareSection && primaryRange?.start) {
                                            setPrimaryCalendarDate(primaryRange.start.startOf("month"));
                                        }
                                    }}
                                />
                                <div className="date-format-display">{dateFormatForDisplay}</div>
                                <div
                                    className={`input-error-message ${validationState[endValidationKey]?.invalid ? "" : "hidden"
                                        }`}
                                >
                                    {validationState[endValidationKey]?.message}
                                </div>
                            </div>
                        </div>

                    </div>
                )}
            </div>
        );
    };

    // Color legend component for footer
    const ColorLegend = () => {
        return (
            <div>
                <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <div className="primary-range-indicator"></div>
                        <span className="small">Primary Range</span>
                    </div>
                    {enableComparison && (
                        <div className="d-flex align-items-center gap-2">
                            <div className="compare-range-indicator"></div>
                            <span className="small">Compare Range</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Validate that there's no overlap between primary and compare ranges
    const hasOverlap = (range1: DateRange, range2: DateRange): boolean => {
        if (!range1.start || !range1.end || !range2.start || !range2.end) return false;

        // Disallow *any* overlap including boundaries (inclusive ranges)
        return (
            (range1.start.isBefore(range2.end, "day") || range1.start.isSame(range2.end, "day")) &&
            (range2.start.isBefore(range1.end, "day") || range2.start.isSame(range1.end, "day"))
        );
    };

    const disabledDate = (date: Dayjs) => {
        // Future dates are always disabled
        if (date.isAfter((dayjs()).subtract(1, "day").endOf("day"))) {
            return true;
        }

        // For compare range in custom mode, disable dates >= primary start
        if (
            focusedInput === DATE_PICKER_FOCUSED_INPUT.COMPARE && 
            comparePeriod === DATE_PICKER_PRESET_PERIODS.CUSTOM && 
            primaryRange.start
        ) {
            return date.isAfter(primaryRange.start) || date.isSame(primaryRange.start, 'day');
        }

        return false;
    };

    return (
        <div className="row p-0 m-0">
            <div className="row p-0 m-0">
                <div
                    className="col-md-8 m-0 pt-3"
                    style={{ borderRight: "2px solid #e8e8e8" }}
                >
                    <div className="d-flex align-items-center justify-content-between mb-3 px-3">
                        <div className="d-flex align-items-center gap-2">
                            <span
                                role="button"
                                style={{ fontSize: 22 }}
                                onClick={() => {
                                    if (focusedInput === DATE_PICKER_FOCUSED_INPUT.COMPARE && comparePeriod === DATE_PICKER_PRESET_PERIODS.CUSTOM) {
                                        setCompareCalendarDate(compareCalendarDate.subtract(1, "month"));
                                    } else {
                                        setPrimaryCalendarDate(primaryCalendarDate.subtract(1, "month"));
                                    }
                                }}
                            >
                                &larr;
                            </span>
                        </div>
                        <div className="d-flex align-items-center gap-4">
                            <h3 className="h6 fw-medium mb-0">
                                {(focusedInput === DATE_PICKER_FOCUSED_INPUT.COMPARE && comparePeriod === DATE_PICKER_PRESET_PERIODS.CUSTOM
                                    ? compareCalendarDate
                                    : primaryCalendarDate
                                ).format(DateTimeFormats.DATE_PICKER_CALENDAR)}
                            </h3>
                            <h3 className="h6 fw-medium mb-0">
                                {(focusedInput === DATE_PICKER_FOCUSED_INPUT.COMPARE && comparePeriod === DATE_PICKER_PRESET_PERIODS.CUSTOM
                                    ? compareCalendarDate
                                    : primaryCalendarDate
                                ).add(1, "month").format(DateTimeFormats.DATE_PICKER_CALENDAR)}
                            </h3>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <span
                                role="button"
                                style={{ fontSize: 22 }}
                                onClick={() => {
                                    if (focusedInput === DATE_PICKER_FOCUSED_INPUT.COMPARE && comparePeriod === DATE_PICKER_PRESET_PERIODS.CUSTOM) {
                                        setCompareCalendarDate(compareCalendarDate.add(1, "month"));
                                    } else {
                                        setPrimaryCalendarDate(primaryCalendarDate.add(1, "month"));
                                    }
                                }}
                            >
                                &rarr;
                            </span>
                        </div>
                    </div>
                    <div className="row g-0 mx-0">
                        <div className="col-6 m-0 px-2">
                            <Calendar
                                value={
                                    focusedInput === DATE_PICKER_FOCUSED_INPUT.COMPARE
                                        ? compareCalendarDate
                                        : primaryCalendarDate
                                }
                                fullCellRender={dualRangeDateCellRender}
                                headerRender={() => null}
                                className="border-0"
                            />
                        </div>
                        <div className="col-6 m-0 px-2">
                            <Calendar
                                value={(focusedInput === DATE_PICKER_FOCUSED_INPUT.COMPARE
                                    ? compareCalendarDate
                                    : primaryCalendarDate
                                ).add(1, "month")}
                                fullCellRender={dualRangeDateCellRender}
                                headerRender={() => null}
                                className="border-0"
                            />
                        </div>
                    </div>
                </div>
                <div className="col-md-4 m- p-0">
                    <div className="date-picker-panel-container">
                        {dateRangeComponent(
                            "Date Range",
                            [
                                { label: "Yesterday", value: DATE_PICKER_PRESET_PERIODS.YESTERDAY },
                                { label: "Last 7 Days", value: DATE_PICKER_PRESET_PERIODS.LAST_7_DAYS },
                                { label: "Last 30 Days", value: DATE_PICKER_PRESET_PERIODS.LAST_30_DAYS },
                                { label: "Month to Date", value: DATE_PICKER_PRESET_PERIODS.MONTH_TO_DATE },
                                { label: "Last Month", value: DATE_PICKER_PRESET_PERIODS.LAST_MONTH },
                                { label: "Last 3 Months", value: DATE_PICKER_PRESET_PERIODS.LAST_3_MONTHS },
                                { label: "Year to Date", value: DATE_PICKER_PRESET_PERIODS.YEAR_TO_DATE },
                                { label: "Last 12 Months", value: DATE_PICKER_PRESET_PERIODS.LAST_12_MONTHS },
                                { label: "Custom", value: DATE_PICKER_PRESET_PERIODS.CUSTOM },
                            ],
                            primaryPeriod,
                            handlePrimaryPeriodChange,
                            primaryRange,
                            setPrimaryRange,
                            false
                        )}
                        <hr className="custom-hr m-0" />
                        {dateRangeComponent(
                            "Compare to",
                            [
                                {
                                    label: "Previous Period",
                                    value: "previous-period",
                                },
                                { label: "Custom", value: DATE_PICKER_PRESET_PERIODS.CUSTOM },
                            ],
                            comparePeriod,
                            handleComparePeriodChange,
                            compareRange,
                            setCompareRange,
                            true
                        )}
                    </div>
                </div>
            </div>
            <div className="row p-0 m-0">
                <div className="col-12 p-0 m-0">
                    <hr className="custom-hr m-0" />
                    <div className="d-flex justify-content-between align-items-center px-3 py-3">
                        <ColorLegend />
                        <small style={{ color: "red" }}>{dateRangeError ? dateRangeError : ""}</small>
                        <div className="d-flex gap-3">
                            <Button
                                className="btn-primary-outline"
                                size="middle"
                                onClick={onCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="revstack-btn-primary"
                                size="middle"
                                onClick={handleApply}
                                disabled={hasValidationErrors()}
                            >
                                Apply
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RevstackRangeDatePicker;
